import React, {useCallback, useMemo, useState, useEffect} from "react"
import PropTypes from "prop-types"
import {Box} from "@mui/material"
import {DataGrid, useGridApiRef} from "@mui/x-data-grid"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import ContentPasteIcon from "@mui/icons-material/ContentPaste"
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import ButtonWithIcon from "../Button/Button"
import theme from "../../theme/theme"
import TextField from "@mui/material/TextField"

const createEmptyRow = (idx = 0, fields = []) => {
    const base = {id: `${Date.now()}-${idx}-${Math.random().toString(16).slice(2)}`}
    fields.forEach((key) => {
        base[key] = ""
    })
    return base
}

const getRowIdValue = (row) => {
    if (!row) return ""
    if (row.id !== undefined && row.id !== null) return row.id
    if (row.ID !== undefined && row.ID !== null) return row.ID
    if (row.Id !== undefined && row.Id !== null) return row.Id
    return undefined
}

const InlineTextCell = ({params, onChangeValue, onBlurPersist}) => {
    const value = params?.value || ""
    const [localValue, setLocalValue] = useState(value)

    useEffect(() => {
        setLocalValue(value)
    }, [value])

    const handleChange = (event) => {
        setLocalValue(event.target.value)
    }
    const handleBlur = async (event) => {
        const newValue = event.target.value
        onChangeValue(params.id, params.field, newValue)
        await onBlurPersist(params.id, params.field, newValue, params.row)
    }
    const handleKeyDown = (event) => {
        const keysToStop = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"]
        if (keysToStop.includes(event.key)) {
            event.stopPropagation()
        }
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "a") {
            event.stopPropagation()
        }
    }
    return (
        <TextField
            variant="standard"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            fullWidth
            autoFocus={false}
            InputProps={{disableUnderline: true, sx: {fontSize: "14px"}}}
        />
    )
}

InlineTextCell.propTypes = {
    params: PropTypes.object.isRequired,
    onChangeValue: PropTypes.func.isRequired,
    onBlurPersist: PropTypes.func.isRequired,
}

const GridFormSheet = ({
    rows,
    onChange,
    columnsOrder,
    columnsConfig,
    onPersistRow,
    onDeleteRow,
    onRowUpdateError,
    alwaysEditable = false,
    pasteMode = "replace",
    onRowsPasted,
    hideAddRow = false,
    showClear = false,
    onClear,
    pasteIcon,
    copyIcon,
    clearIcon,
    rowHeight: customRowHeight,
    sx: outerSx,
}) => {
    const originalRowRef = React.useRef({})
    const apiRef = useGridApiRef()
    // const ensureAtLeastOneRow = useCallback(() => {
    //     if (!rows.length) {
    //         onChange([createEmptyRow(0, columnsOrder || [])])
    //     }
    // }, [columnsOrder, onChange, rows.length])

    const deletingRef = React.useRef(new Set())
    // garante linha inicial em branco
    // React.useEffect(() => {
    //     if (!rows.length) {
    //         onChange([createEmptyRow(0, columnsOrder || [])])
    //     }
    // }, [])

    const ensureFocus = useCallback(
        (rowId, field, delay = 30) => {
            setTimeout(() => {
                apiRef.current?.setCellFocus(rowId, field)
                if (!alwaysEditable) {
                    apiRef.current?.startCellEditMode({id: rowId, field})
                } else {
                    const cellEl = apiRef.current?.getCellElement?.(rowId, field)
                    const inputEl =
                        cellEl?.querySelector?.("input, textarea, select, [tabindex]:not([tabindex='-1'])")
                    inputEl?.focus?.({preventScroll: true})
                }
            }, delay)
        },
        [alwaysEditable, apiRef]
    )

    const handleDelete = useCallback(
        async (id, event) => {
            event?.preventDefault?.()
            event?.stopPropagation?.()
            event?.nativeEvent?.stopImmediatePropagation?.()
            if (event) {
                event.defaultMuiPrevented = true
            }
            if (deletingRef.current.has(id)) return
            deletingRef.current.add(id)

            let scheduled = false
            try {
                const targetRowIndex = rows.findIndex((row) => getRowIdValue(row) === id)
                if (targetRowIndex < 0) return

                const targetRow = rows[targetRowIndex]
                if (targetRow && onDeleteRow) {
                    const result = await onDeleteRow(targetRow)
                    if (result === false) return
                }

                try {
                    const editingField = columnsOrder?.find(
                        (field) => apiRef.current?.getCellMode?.(id, field) === "edit"
                    )
                    if (editingField) {
                        apiRef.current?.stopCellEditMode?.({
                            id,
                            field: editingField,
                            ignoreModifications: true,
                        })
                    }
                } catch (_err) {
                    // silencioso: evitar erro quando a linha não está em modo de edição
                }

                const filtered = rows.filter((row) => getRowIdValue(row) !== id)
                let focusRowId = ""
                const nextRows = filtered.length
                    ? filtered
                    : [createEmptyRow(0, columnsOrder || [])]

                onChange(nextRows)
                delete originalRowRef.current[id]

                if (nextRows.length) {
                    const fallbackIndex = Math.min(targetRowIndex, nextRows.length - 1)
                    focusRowId = getRowIdValue(nextRows[fallbackIndex]) || ""
                    ensureFocus(focusRowId, columnsOrder[0], 60)
                }

                scheduled = true
                const applyDeletion = () => {
                    onChange(nextRows)
                    delete originalRowRef.current[id]
                    if (focusRowId && columnsOrder?.length) {
                        ensureFocus(focusRowId, columnsOrder[0], 60)
                    }
                    deletingRef.current.delete(id)
                }
                // Postpone to next frame to let the grid finish handling the click event
                // and avoid getCellValue on a row already removed.
                if (typeof requestAnimationFrame === "function") {
                    requestAnimationFrame(() => setTimeout(applyDeletion, 0))
                } else {
                    setTimeout(applyDeletion, 0)
                }
            } finally {
                if (!scheduled) {
                    deletingRef.current.delete(id)
                }
            }
        },
        [apiRef, columnsOrder, ensureFocus, onChange, onDeleteRow, rows]
    )

    const handleAddRow = useCallback(() => {
        let newRowId = ""
        onChange((prev) => {
            const newRow = createEmptyRow(prev.length, columnsOrder || [])
            newRowId = newRow.id
            return [...prev, newRow]
        })
        return newRowId
    }, [columnsOrder, onChange])

    const handleInlineChange = useCallback(
        (id, field, value) => {
            onChange((prev) => {
                const base = Array.isArray(prev) ? [...prev] : []
                const idx = base.findIndex((row) => getRowIdValue(row) === id)
                if (idx >= 0) {
                    if (!originalRowRef.current[id]) {
                        originalRowRef.current[id] = base[idx]
                    }
                    base[idx] = {...base[idx], [field]: value}
                }
                return base
            })
        },
        [columnsOrder, onChange]
    )

    const handleInlineBlur = useCallback(
        async (id, field, value, rowSnapshot) => {
            if (!onPersistRow) return
            const baseOriginal = originalRowRef.current[id] || rowSnapshot
            const newRow = {...rowSnapshot, [field]: value}
            const result = await onPersistRow(newRow, baseOriginal)
            if (result && typeof result === "object") {
                onChange((prev) =>
                    prev.map((row) => (getRowIdValue(row) === id ? {...row, ...result} : row))
                )
            }
            delete originalRowRef.current[id]
        },
        [onChange, onPersistRow]
    )

    const inlineHandlers = useMemo(
        () => ({
            onChangeValue: handleInlineChange,
            onBlurPersist: handleInlineBlur,
        }),
        [handleInlineBlur, handleInlineChange]
    )

    const columns = useMemo(
        () => [
            ...(columnsConfig || []).map((col) => ({
                ...col,
                flex: col.flex ?? 1,
                editable: alwaysEditable ? false : col.editable ?? true,
                renderCell:
                    alwaysEditable && col.field !== "acoes"
                        ? col.renderInlineCell
                            ? (params) => (
                                <Box
                                    sx={{width: "100%", height: "100%", display: "flex", alignItems: "center"}}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        const input = e.currentTarget.querySelector(
                                            "input, textarea, select, [tabindex]:not([tabindex='-1'])"
                                        )
                                        input?.focus?.()
                                    }}
                                >
                                    {col.renderInlineCell(params, inlineHandlers)}
                                </Box>
                            )
                            : (params) => (
                                <Box
                                    sx={{width: "100%", height: "100%", display: "flex", alignItems: "center"}}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        const input = e.currentTarget.querySelector(
                                            "input, textarea, select, [tabindex]:not([tabindex='-1'])"
                                        )
                                        input?.focus?.()
                                    }}
                                >
                                    <InlineTextCell
                                        params={params}
                                        onChangeValue={handleInlineChange}
                                        onBlurPersist={handleInlineBlur}
                                    />
                                </Box>
                            )
                        : col.renderCell,
            })),
            {
                field: "acoes",
                headerName: "",
                sortable: false,
                filterable: false,
                width: 80,
                editable: false,
                renderCell: (params) => (
                    <ButtonWithIcon
                        size="sm"
                        icon={<DeleteOutlineIcon fontSize="small" />}
                        onClick={(event) => handleDelete(params.id, event)}
                        onMouseDown={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            event.nativeEvent?.stopImmediatePropagation?.()
                            event.defaultMuiPrevented = true
                        }}
                        sx={{height: 32}}
                        tabIndex={-1}
                        disableRipple
                    >
                    </ButtonWithIcon>
                ),
            },
        ],
        [alwaysEditable, columnsConfig, handleDelete, handleInlineBlur, handleInlineChange, inlineHandlers]
    )

    const pasteText = useCallback(
        (text, {startRowIndex = 0, startColIndex = 0, mode = "replace"} = {}) => {
            if (!text) return
            const lines = text
                .replace(/\r/g, "")
                .split("\n")
                .filter((line) => line.length > 0)
                .map((line) => line.split("\t"))

            if (!lines.length) return

            let pastedRows = []

            onChange((prev) => {
                const base = [...prev]
                const hasSingleEmptyRow =
                    mode === "append" &&
                    base.length === 1 &&
                    (columnsOrder || []).every((col) => `${base[0]?.[col] || ""}`.trim().length === 0)

                const effectiveStartRowIndex = mode === "append"
                    ? (hasSingleEmptyRow ? 0 : base.length)
                    : startRowIndex

                while (base.length < effectiveStartRowIndex + lines.length) {
                    base.push(createEmptyRow(base.length, columnsOrder || []))
                }

                lines.forEach((cells, rowOffset) => {
                    const targetRowIndex = effectiveStartRowIndex + rowOffset
                    const row = {...base[targetRowIndex]}

                    cells.forEach((cellValue, cellIdx) => {
                        const targetColIndex = startColIndex + cellIdx
                        if (targetColIndex >= columnsOrder.length) return
                        row[columnsOrder[targetColIndex]] = cellValue
                    })

                    base[targetRowIndex] = row
                })
                const nextRows = base.length ? base : [createEmptyRow(0, columnsOrder || [])]
                pastedRows = nextRows.slice(effectiveStartRowIndex, effectiveStartRowIndex + lines.length)
                return nextRows
            })

            if (onRowsPasted && pastedRows.length) {
                onRowsPasted(pastedRows)
            }
        },
        [columnsOrder, onChange, onRowsPasted]
    )

    const handlePaste = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText()
            if (!text) return
            pasteText(text, {startRowIndex: 0, startColIndex: 0, mode: pasteMode})
        } catch (_err) {
            // silencioso
        }
    }, [pasteMode, pasteText])

    const handleCopy = useCallback(async () => {
        if (!rows || !rows.length) return

        const header = columnsOrder.join("\t")

        const body = rows.map((row) =>
            columnsOrder
                .map((col) => `${row[col] ?? ""}`)
                .join("\t")
        )

        const textToCopy = [header, ...body].join("\n")

        try {
            await navigator.clipboard.writeText(textToCopy)
        } catch (err) {
            // opcional: tratar erro
        }
    }, [rows, columnsOrder])


    React.useEffect(() => {
        const missingId = rows.some((row) => getRowIdValue(row) === undefined)
        if (missingId) {
            onChange((prev) =>
                prev.map((row, idx) => {
                    const existing = getRowIdValue(row)
                    if (existing !== undefined) return row
                    return {
                        ...row,
                        id: `${Date.now()}-${idx}-${Math.random().toString(16).slice(2)}`,
                    }
                })
            )
        }
    }, [onChange, rows])

    const handleCellPaste = useCallback(
        async (params) => {
            try {
                const text = await navigator.clipboard.readText()
                if (!text) return
                const startRowIndex = params.id ? rows.findIndex((r) => r.id === params.id) : 0
                const startColIndex = Math.max(columnsOrder.indexOf(params.field || columnsOrder[0]), 0)
                pasteText(
                    text,
                    {
                        startRowIndex: startRowIndex < 0 ? 0 : startRowIndex,
                        startColIndex,
                        mode: pasteMode,
                    }
                )
            } catch (_err) {
                // silencioso
            }
        },
        [columnsOrder, pasteMode, pasteText, rows]
    )

    const handleCellKeyDown = useCallback(
        (params, event) => {
            if (event.ctrlKey && (event.key === "v" || event.key === "V")) {
                event.preventDefault()
                event.defaultMuiPrevented = true
                event.stopPropagation()
                handleCellPaste(params)
                return
            }

            if (params.field === "acoes" && event.key === "Tab") {
                event.preventDefault()
                event.defaultMuiPrevented = true
                event.stopPropagation()
                const currentRowIndex = rows.findIndex((r) => r.id === params.id)
                const nextRow = rows[currentRowIndex + 1]
                if (nextRow) {
                    ensureFocus(nextRow.id, columnsOrder[0])
                } else {
                    const newRowId = handleAddRow()
                    ensureFocus(newRowId || params.id, columnsOrder[0], 120)
                }
                return
            }

            if (event.key === "Tab" && !event.shiftKey) {
                event.preventDefault()
                event.defaultMuiPrevented = true
                event.stopPropagation()
                const currentColIndex = columnsOrder.indexOf(params.field)
                const nextColIndex = currentColIndex + 1
                const lastColIndex = columnsOrder.length - 1

                if (nextColIndex <= lastColIndex) {
                    const nextField = columnsOrder[nextColIndex]
                    ensureFocus(params.id, nextField)
                } else {
                    const currentRowIndex = rows.findIndex((r) => r.id === params.id)
                    const nextRow = rows[currentRowIndex + 1]
                    if (nextRow) {
                        ensureFocus(nextRow.id, columnsOrder[0])
                    } else {
                        const newRowId = handleAddRow()
                        const targetId = newRowId || params.id
                        ensureFocus(targetId, columnsOrder[0], 120)
                    }
                }
            }
        },
        [columnsOrder, ensureFocus, handleAddRow, handleCellPaste, rows]
    )

    const handleProcessRowUpdate = useCallback(
        async (newRow, oldRow) => {
            let finalRow = {...newRow}
            if (onPersistRow) {
                const persisted = await onPersistRow(finalRow, oldRow)
                if (persisted && typeof persisted === "object") {
                    finalRow = {...finalRow, ...persisted}
                }
            }
            onChange((prev) => {
                const base = Array.isArray(prev) ? [...prev] : []
                const targetId = getRowIdValue(oldRow) ?? getRowIdValue(newRow)
                const idx = base.findIndex((row) => getRowIdValue(row) === targetId)
                if (idx >= 0) {
                    base[idx] = {...base[idx], ...finalRow}
                } else {
                    base.push(finalRow)
                }
                return base
            })
            return finalRow
        },
        [onChange, onPersistRow]
    )

    const handleRowUpdateError = useCallback(
        (error) => {
            if (onRowUpdateError) {
                onRowUpdateError(error)
            }
        },
        [onRowUpdateError]
    )

    return (
        <>
            <Box sx={{display: "flex", justifyContent: showClear ? "space-between" : "flex-end", gap: 2, mb: 1}}>
                {showClear && (
                    <ButtonWithIcon size="md" icon={clearIcon || <DeleteOutlineIcon />} onClick={onClear}>
                        LIMPAR
                    </ButtonWithIcon>
                )}
                <Box sx={{display: "flex", gap: 2}}>
                    <ButtonWithIcon size="md" icon={copyIcon || <ContentCopyIcon />} onClick={handleCopy}>
                        COPIAR
                    </ButtonWithIcon>
                    <ButtonWithIcon size="md" icon={pasteIcon || <ContentPasteIcon />} onClick={handlePaste}>
                        COLAR
                    </ButtonWithIcon>
                    {!hideAddRow && (
                        <ButtonWithIcon size="md" icon={<AddCircleOutlineIcon />} onClick={handleAddRow}>
                            ADICIONAR LINHA
                        </ButtonWithIcon>
                    )}
                </Box>
            </Box>
            <Box
                sx={[
                    {
                        marginTop: 1,
                        border: `1px solid ${theme.palette.input.borderColor.primary}`,
                        borderBottom: 0,
                        maxHeight: 1000,
                        overflow: "hidden",
                        ".MuiDataGrid-root": {
                            border: "none",
                            backgroundColor: "transparent",
                            color: theme.palette.common.white,
                            fontSize: "14px",
                            boxShadow: "none !important",
                            height: "100%",
                        },
                        ".MuiDataGrid-main": {
                            maxHeight: 1000,
                            overflow: "hidden",
                        },
                        ".MuiDataGrid-columnHeaders": {
                            backgroundColor: theme.palette.background.paper,
                            borderBottom: `1px solid ${theme.palette.common.white}`,
                            minHeight: customRowHeight || 64,
                            maxHeight: customRowHeight || 64,
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                        },
                        ".MuiDataGrid-columnHeader": {
                            borderRight: `1px solid ${theme.palette.common.white}`,
                            "&:focus, &:focus-within": {
                                outline: "none !important",
                                boxShadow: "none !important",
                                borderColor: theme.palette.common.white,
                                backgroundColor: "transparent",
                            },
                            ".MuiDataGrid-sortIcon": {
                                color: theme.palette.common.white,
                            },
                        },
                        ".MuiDataGrid-columnHeader:last-of-type": {
                            borderRight: 0,
                        },
                        ".MuiDataGrid-columnHeaderTitle": {
                            fontWeight: 700,
                            textTransform: "uppercase",
                            fontSize: "14px",
                        },
                        ".MuiDataGrid-cell": {
                            borderColor: theme.palette.input.borderColor.primary,
                            alignItems: "center",
                            color: theme.palette.common.white,
                            borderRight: `1px solid ${theme.palette.input.borderColor.primary}`,
                        },
                        ".MuiDataGrid-row .MuiDataGrid-cell:last-of-type": {
                            borderRight: 0,
                        },
                        ".MuiDataGrid-row:last-of-type .MuiDataGrid-cell": {
                            borderBottom: `1px solid ${theme.palette.input.borderColor.primary}`,
                        },
                        ".MuiDataGrid-virtualScrollerRenderZone": {
                            ".MuiDataGrid-row:last-of-type .MuiDataGrid-cell": {
                                borderBottom: `1px solid ${theme.palette.input.borderColor.primary}`,
                            },
                        },
                        ".MuiDataGrid-cell:focus": {
                            outline: "0 !important"
                        },
                        ".MuiDataGrid-virtualScroller": {
                            backgroundColor: "transparent",
                            maxHeight: `calc(1000px - ${customRowHeight || 64}px)`,
                            overflowY: "auto !important",
                            overflowX: "hidden",
                            paddingBottom: "1px"
                        },
                        ".MuiDataGrid-columnSeparator, .MuiDataGrid-iconSeparator": {
                            display: "none",
                        },
                        ".MuiDataGrid-withBorderColor": {
                            borderColor: theme.palette.input.borderColor.primary,
                        },
                        ".MuiInputBase-input": {
                            color: theme.palette.common.white,
                            backgroundColor: "transparent",
                            paddingY: 0,
                        },
                        ".MuiOutlinedInput-root": {
                            backgroundColor: "transparent",
                            boxShadow: "none",
                        },
                        ".MuiDataGrid-cell--editing": {
                            boxShadow: "none !important",
                            color: "none !important",
                            outline: "none !important",
                            outlineOffset: "0 !important"
                        },
                        ".MuiDataGrid-cell:focus-within, .MuiDataGrid-cell--editing:focus-within": {
                            outline: "none !important",
                            outlineOffset: "0 !important"
                        },
                    },
                    outerSx,
                ]}
            >
                <DataGrid
                    apiRef={apiRef}
                    rows={rows}
                    getRowId={(row) => {
                        const existing = getRowIdValue(row)
                        if (existing !== undefined) return existing
                        const composite = (columnsOrder || [])
                            .map((col) => `${row[col] || ""}`)
                            .join("|")
                        return composite || JSON.stringify(row)
                    }}
                    columns={columns}
                    disableRowSelectionOnClick
                    hideFooter
                    processRowUpdate={handleProcessRowUpdate}
                    onProcessRowUpdateError={handleRowUpdateError}
                    experimentalFeatures={{newEditingApi: true}}
                    onCellKeyDown={handleCellKeyDown}
                    disableColumnMenu
                    disableColumnFilter
                    disableColumnSelector
                    rowHeight={customRowHeight || 64}
                    columnHeaderHeight={customRowHeight || 64}
                    editMode="cell"
                    isCellEditable={(params) => params.field !== "acoes"}
                    scrollbarSize={0}
                />
            </Box>
        </>
    )
}

GridFormSheet.propTypes = {
    rows: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    columnsOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
    columnsConfig: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            headerName: PropTypes.string,
            flex: PropTypes.number,
            width: PropTypes.number,
            editable: PropTypes.bool,
            renderInlineCell: PropTypes.func,
        })
    ).isRequired,
    onPersistRow: PropTypes.func,
    onDeleteRow: PropTypes.func,
    onRowUpdateError: PropTypes.func,
    alwaysEditable: PropTypes.bool,
    pasteMode: PropTypes.oneOf(["replace", "append"]),
    onRowsPasted: PropTypes.func,
    hideAddRow: PropTypes.bool,
    showClear: PropTypes.bool,
    onClear: PropTypes.func,
    pasteIcon: PropTypes.node,
    copyIcon: PropTypes.node,
    clearIcon: PropTypes.node,
    rowHeight: PropTypes.number,
    sx: PropTypes.object,
}

export default GridFormSheet
